import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { disableSearch, enableSearch, getSearchStatus } from "../api";
import { useT } from "../i18n";

/**
 * SearchPanel is the switch for whether readers can search this book.
 *
 * Off by default, and deliberately (ADR-0014). Turning it on sends the whole
 * text to the model provider to be indexed, which is a thing to decide rather
 * than a consequence of uploading a chapter — and an unpublished manuscript
 * has every reason not to leave the building.
 *
 * The panel's job is to make the difference legible before the tap: what
 * readers get either way, how much is out of date, and what refreshing costs.
 * The cost is beside the button that spends it, the way the narration estimate
 * is.
 */
export default function SearchPanel({ bookID, bare = false }: { bookID: string; bare?: boolean }) {
  const client = useQueryClient();
  const t = useT();
  const status = useQuery({
    queryKey: ["search", bookID],
    queryFn: () => getSearchStatus(bookID),
  });

  const done = () => client.invalidateQueries({ queryKey: ["search", bookID] });
  const enable = useMutation({
    mutationFn: () => enableSearch(bookID),
    onSuccess: done,
  });
  const disable = useMutation({
    mutationFn: () => disableSearch(bookID),
    onSuccess: done,
  });

  const s = status.data;
  const busy = enable.isPending || disable.isPending;
  const failed = enable.error ?? disable.error;

  return (
    <section
      className={bare ? "" : "mt-10 border-t border-line pt-5"}
      data-testid="search-panel"
      data-grounded={s?.grounded ? "true" : "false"}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">{t.search.heading}</h2>
          {/* Both halves, always. A reader is never left without an answer —
              what changes is whether the answer is anchored in the book or
              reasoned from the page they are on. */}
          <p className="mt-1 max-w-prose text-xs leading-5 text-muted">
            {s?.grounded ? t.search.on : t.search.off}
          </p>
        </div>

        {s && (
          <button
            type="button"
            data-testid={s.grounded ? "disable-search" : "enable-search"}
            disabled={busy}
            onClick={() => (s.grounded ? disable.mutate() : enable.mutate())}
            className={
              s.grounded
                ? "shrink-0 rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:bg-sunk disabled:opacity-40"
                : "shrink-0 rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-on-sage transition-colors hover:bg-sage-deep disabled:opacity-40"
            }
          >
            {busy ? t.search.working : s.grounded ? t.search.turnOff : t.search.turnOn}
          </button>
        )}
      </div>

      {s && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
          {s.grounded && (
            <span data-testid="search-indexed">{t.search.indexed(s.indexed, s.chapters)}</span>
          )}

          {/* Out of date is the honest cost of not re-indexing on every save.
              It has to be visible, or a reader gets citations into text the
              author rewrote weeks ago and nobody knows. */}
          {s.grounded && s.stale > 0 && (
            <button
              type="button"
              data-testid="refresh-search"
              disabled={busy}
              onClick={() => enable.mutate()}
              className="rounded-control border border-amber-600 px-2.5 py-1 text-amber-700 transition-colors hover:bg-amber-500/10 disabled:opacity-40 dark:border-amber-400 dark:text-amber-400"
            >
              {t.search.stale(s.stale, s.estimate_display)}
            </button>
          )}

          {!s.grounded && s.stale > 0 && (
            <span data-testid="search-estimate">{t.search.estimate(s.estimate_display)}</span>
          )}

          {s.grounded && !s.searchable && (
            <span data-testid="search-pending">{t.search.pending}</span>
          )}
        </div>
      )}

      {failed && (
        <p
          role="alert"
          data-testid="search-error"
          className="mt-2 text-sm text-red-600 dark:text-red-400"
        >
          {failed instanceof Error ? failed.message : t.search.changeFailed}
        </p>
      )}
    </section>
  );
}
